import Animated, {
  interpolate,
  useAnimatedStyle,
  useDerivedValue,
  withTiming,
} from 'react-native-reanimated';
import i18n from '@/utils/i18n';
import {
  Button,
  DropdownMenu,
  Icons,
  Typography,
  XStack,
  YStack,
} from '@usepepper/sprout-kit-ui';
import { DEFAULT_EASING_IN_OUT_CONFIG } from '@experiences/blocs/utils/animations';
import { OrderGuideCollection } from '@/experiences/sales-portal/domain/order-guides/screens/order-guides-list/types';
import { useState } from 'react';
import { useTheme } from '@/contexts/theme-context';

const MAX_DROPDOWN_HEIGHT = 400;
const DROPDOWN_WIDTH = 300;
// Cap the guide name so the trigger and rows stay within DROPDOWN_WIDTH,
// leaving room for the chevron/lock icon and padding before truncating.
const GUIDE_NAME_MAX_WIDTH = 230;
const DROPDOWN_ITEM_ICON_SIZE = 16;

export interface OrderGuideSelectorOption extends OrderGuideCollection {
  isViewOnly: boolean;
}

interface OrderGuideSelectorDropdownProps {
  canCreateGuides?: boolean;
  onCreateOrderGuide: () => void;
  onSelectOrderGuide: (orderGuide: OrderGuideSelectorOption) => void;
  orderGuides: OrderGuideSelectorOption[];
  selectedOrderGuide: OrderGuideCollection | null;
}

/**
 * Order guide selector for the OG management screen. Lists the selected
 * customer's order guides and allows users to create new order guides
 */
export const OrderGuideSelectorDropdown: React.FC<
  OrderGuideSelectorDropdownProps
> = ({
  onCreateOrderGuide,
  onSelectOrderGuide,
  orderGuides,
  selectedOrderGuide,
  canCreateGuides = false,
}) => {
  const theme = useTheme();
  const [isOpen, setIsOpen] = useState(false);

  const rotationProgress = useDerivedValue(() =>
    withTiming(isOpen ? 1 : 0, DEFAULT_EASING_IN_OUT_CONFIG),
  );
  const chevronStyle = useAnimatedStyle(() => ({
    transform: [
      { rotate: `${interpolate(rotationProgress.value, [0, 1], [0, 180])}deg` },
    ],
  }));

  const handleSelectOrderGuide = (og: OrderGuideSelectorOption) => {
    onSelectOrderGuide(og);
    setIsOpen(false);
  };

  const handleCreateOrderGuide = () => {
    onCreateOrderGuide();
    setIsOpen(false);
  };

  return (
    <DropdownMenu onOpenChange={setIsOpen} open={isOpen}>
      <DropdownMenu.Trigger asChild>
        <Button
          alignSelf="center"
          maxWidth={DROPDOWN_WIDTH}
          size="$sm"
          variant="secondary"
        >
          <XStack alignItems="center" gap="$8">
            <XStack maxWidth={GUIDE_NAME_MAX_WIDTH} overflow="hidden">
              <Typography
                color={theme.sk.color.text.primary}
                fontWeight="$medium"
                numberOfLines={1}
                size="$sm"
              >
                {selectedOrderGuide?.displayName ??
                  i18n.t(
                    'sales_portal.order_guides_list.guide_selector.placeholder',
                  )}
              </Typography>
            </XStack>
            <Animated.View style={chevronStyle}>
              <Icons.ChevronDown
                color={theme.sk.color.icon.tertiary}
                flexShrink={0}
                size="$14"
              />
            </Animated.View>
          </XStack>
        </Button>
      </DropdownMenu.Trigger>
      {/* Prevent the focus scope from auto-focusing the first item on open:
          its `focusStyle` paints the same `$bgDropdownHover` we use for the
          selected row, so the first item would always look selected. */}
      <DropdownMenu.Content
        maxHeight={MAX_DROPDOWN_HEIGHT}
        onOpenAutoFocus={(event: Event) => event.preventDefault()}
        width={DROPDOWN_WIDTH}
      >
        <DropdownMenu.Label>
          {i18n.t('sales_portal.order_guides_list.page_title')}
        </DropdownMenu.Label>
        {orderGuides.map((guide) => {
          const isSelected = guide.uuid === selectedOrderGuide?.uuid;

          return (
            <DropdownMenu.Item
              accessibilityState={{ selected: isSelected }}
              backgroundColor={isSelected ? '$bgDropdownHover' : '$transparent'}
              height="auto"
              key={guide.uuid}
              onPress={() => handleSelectOrderGuide(guide)}
              paddingVertical={theme.sk.spacing.spaceSmall}
            >
              <YStack alignItems="flex-start">
                <XStack alignItems="center" gap={theme.sk.spacing.spaceSmall}>
                  <XStack maxWidth={GUIDE_NAME_MAX_WIDTH} overflow="hidden">
                    <Typography
                      color={theme.sk.color.text.primary}
                      fontWeight="$semibold"
                      numberOfLines={1}
                      size="$sm"
                    >
                      {guide.displayName}
                    </Typography>
                  </XStack>
                  {guide.isViewOnly ? (
                    <Icons.Lock
                      accessibilityLabel={i18n.t(
                        'sales_portal.order_guides_list.view_only.label',
                      )}
                      color={theme.sk.color.icon.tertiary}
                      size={DROPDOWN_ITEM_ICON_SIZE}
                    />
                  ) : null}
                </XStack>
                {guide.groupCount !== null ? (
                  <Typography color={theme.sk.color.text.tertiary} size="$xs">
                    {i18n.t(
                      guide.groupCount === 1
                        ? 'sales_portal.order_guides_list.guide_selector.group_count_singular'
                        : 'sales_portal.order_guides_list.guide_selector.group_count_plural',
                      { count: guide.groupCount },
                    )}
                  </Typography>
                ) : null}
              </YStack>
            </DropdownMenu.Item>
          );
        })}
        {canCreateGuides ? (
          <>
            <DropdownMenu.Separator />
            <DropdownMenu.Label>
              {i18n.t(
                'sales_portal.order_guides_list.guide_selector.create_section_label',
              )}
            </DropdownMenu.Label>
            <DropdownMenu.Item
              height="auto"
              onPress={handleCreateOrderGuide}
              paddingVertical={theme.sk.spacing.spaceSmall}
            >
              <XStack alignItems="center" gap={theme.sk.spacing.spaceSmall}>
                <Icons.Plus
                  color={theme.sk.color.icon.tertiary}
                  size={DROPDOWN_ITEM_ICON_SIZE}
                />
                <YStack alignItems="flex-start">
                  <Typography
                    color={theme.sk.color.text.primary}
                    size="$sm"
                    variant="label"
                  >
                    {i18n.t(
                      'sales_portal.order_guides_list.guide_selector.create_from_scratch_title',
                    )}
                  </Typography>
                  <Typography
                    color={theme.sk.color.text.tertiary}
                    size="$xs"
                    variant="body"
                  >
                    {i18n.t(
                      'sales_portal.order_guides_list.guide_selector.create_from_scratch_description',
                    )}
                  </Typography>
                </YStack>
              </XStack>
            </DropdownMenu.Item>
          </>
        ) : null}
      </DropdownMenu.Content>
    </DropdownMenu>
  );
};
